import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  TextField,
  Typography,
  Autocomplete,
  Chip,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ro } from "date-fns/locale";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import moment from "moment";
import genericStyles from "../Styles/genericStyles.module.css";
import styles from "../Styles/smsClientsPage.module.css";
import { Vocabulary } from "../Utils/Vocabulary";
import { endpoints } from "../Utils/UrlEnum";
import Config from "../Utils/Config";
import { postData } from "../Services/postData";
import { ETableEditAction } from "../Utils/Enums";
import MUIDataTableCustomStyle from "../Components/MUIDataTableCustomStyle";
import useCustomMemo from "../Hooks/useCustomMemo";
import withRole from "../Hooks/withRole";
import {
  getFullName,
  setFontColorBasedOnBackground,
  fetchProducts,
} from "../Utils/Utils";
import { getFromCacheOrFetch } from "../Utils/getFromCacheOrFetch";

function SmsClientsPage() {
  const memo = useCustomMemo();
  const workPoints = memo?.storage?.workPoints || [];
  const [products, setProducts] = useState<any[]>([]);

  const [selectedWorkPoint, setSelectedWorkPoint] = useState<any>(null);
  const [selectedService, setSelectedService] = useState<any>(null);
  const [smsMessage, setSmsMessage] = useState("");
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [triggerReload, setTriggerReload] = useState<boolean | undefined>();

  const buildFilters = useCallback(() => {
    const nextFilters: Record<string, any> = {};

    if (selectedWorkPoint?._id) {
      nextFilters.workPointId = selectedWorkPoint._id;
    }

    if (selectedService?._id) {
      nextFilters.productId = selectedService._id;
    }

    if (fromDate) {
      nextFilters.start = moment(fromDate).format(Config.momentUSDateFormat);
    }

    if (toDate) {
      nextFilters.end = moment(toDate).format(Config.momentUSDateFormat);
    }

    const searchValue = new URLSearchParams(window.location.search).get(
      "search"
    );
    if (searchValue) {
      nextFilters.search = searchValue;
    }

    return nextFilters;
  }, [fromDate, selectedService, selectedWorkPoint, toDate]);

  const handleApplyFilters = useCallback(() => {
    const nextFilters = buildFilters();
    setFilters(nextFilters);
    setTriggerReload((prev) => !prev);
  }, [buildFilters]);

  useEffect(() => {
    let mounted = true;

    getFromCacheOrFetch<any[]>({
      cacheKey: "products",
      storage: memo.storage,
      setStorage: memo.setStorageData,
      fetcher: fetchProducts(),
      isValid: (data) => Array.isArray(data) && data.length > 0,
    }).then((data) => {
      if (mounted) {
        setProducts(data);
      }
    });

    return () => {
      mounted = false;
    };
  }, [memo]);

  const handleSendSms = useCallback(async () => {
    try {
      if (!smsMessage.trim()) {
        return;
      }

      const filterPayload = buildFilters();

      await postData(endpoints.notifications.sendSMSFiltered, {
        message: smsMessage,
        filters: filterPayload,
      });

      setSmsMessage("");
    } catch (error) {
      console.error("Error sending SMS:", error);
    }
  }, [buildFilters, smsMessage]);

  const clientsColumns = useMemo(
    () => [
      {
        label: ` `,
        name: "_id",
        options: {
          sort: false,
          display: false,
        },
      },
      {
        label: `${Vocabulary.status}`,
        name: "status",
        options: {
          setCellHeaderProps: () => ({
            align: "center",
          }),
          setCellProps: () => ({
            align: "center",
          }),
          filter: false,
          sort: false,
          empty: true,
          customBodyRender: (rowData: any) => {
            return (
              <Chip
                color="primary"
                key={rowData?._id}
                label={rowData?.name}
                style={{
                  margin: 5,
                  backgroundColor: rowData?.color,
                  color: setFontColorBasedOnBackground(rowData?.color),
                }}
              />
            );
          },
        },
      },
      {
        label: `${Vocabulary.instructor}`,
        name: "instructor_id",
        options: {
          sort: false,
          customBodyRender: (rowData: any) => {
            return getFullName(rowData?.user_id);
          },
        },
      },
      {
        label: `${Vocabulary.CNP}`,
        name: "client_id",
        options: {
          sort: false,
          customBodyRender: (rowData: any) => {
            return rowData?.CNP;
          },
        },
      },
      {
        label: `${Vocabulary.clientName}`,
        name: "client_id",
        options: {
          sort: false,
          customBodyRender: (rowData: any) => {
            return (
              <div>
                {`${rowData?.lastName} ${rowData?.firstName} ${
                  !rowData?.lastName && !rowData?.firstName ? "-" : ""
                }`}
              </div>
            );
          },
        },
      },
      {
        label: `${Vocabulary.phone}`,
        name: "client_id",
        options: {
          sort: false,
          customBodyRender: (client: any) => {
            return (
              <>
                {client ? (
                  <a
                    key={client.phone}
                    href={`tel:${client.phone}`}
                    style={{ textDecoration: "none" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Chip
                      icon={<LocalPhoneIcon fontSize="small" />}
                      label={client.phone}
                      style={{ padding: 5 }}
                    />
                  </a>
                ) : null}
              </>
            );
          },
        },
      },
      {
        label: `${Vocabulary.service}`,
        name: "product",
        options: {
          sort: false,
          customBodyRender: (rowData: any) => {
            return <div>{rowData?.name}</div>;
          },
        },
      },
      {
        label: `${Vocabulary.workPoint}`,
        name: "client_id",
        options: {
          sort: false,
          customBodyRender: (rowData: any) => {
            return rowData?.workPointId?.name;
          },
        },
      },
      {
        label: `${Vocabulary.date}`,
        name: "createdAt",
        options: {
          sort: false,
          customBodyRender: (rowData: any) => {
            return moment(rowData).format(Config.momentEUDateFormat);
          },
        },
      },
    ],
    []
  );

  const canSend = smsMessage.trim().length > 0;

  return (
    <div className={genericStyles.mainContainer}>
      <div className={styles.container}>
        <div className={styles.leftPanel}>
          <Typography variant="h6" className={styles.sectionTitle}>
            {Vocabulary.smsClientsTitle}
          </Typography>
          <Divider className={styles.sectionDivider} />

          <Box className={styles.fieldGroup}>
            <Typography className={styles.fieldLabel}>
              {Vocabulary.workPoints}
            </Typography>
            <Autocomplete
              options={workPoints}
              value={selectedWorkPoint}
              onChange={(_, value) => setSelectedWorkPoint(value)}
              getOptionLabel={(option: any) => option?.name || ""}
              renderInput={(params) => (
                <TextField {...params} placeholder={Vocabulary.select} />
              )}
              isOptionEqualToValue={(option: any, value: any) =>
                option?._id === value?._id
              }
            />
          </Box>

          <Box className={styles.fieldGroup}>
            <Typography className={styles.fieldLabel}>
              {Vocabulary.service}
            </Typography>
            <Autocomplete
              options={products}
              value={selectedService}
              onChange={(_, value) => setSelectedService(value)}
              getOptionLabel={(option: any) => option?.name || ""}
              renderInput={(params) => (
                <TextField {...params} placeholder={Vocabulary.select} />
              )}
              isOptionEqualToValue={(option: any, value: any) =>
                option?._id === value?._id
              }
            />
          </Box>

          <Box className={styles.fieldGroup}>
            <Typography className={styles.fieldLabel}>
              {Vocabulary.fromDate}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ro}>
              <DatePicker
                value={fromDate}
                onChange={(date) => setFromDate(date)}
                format="dd-MM-yyyy"
                slotProps={{
                  textField: { size: "medium", fullWidth: true },
                }}
              />
            </LocalizationProvider>
          </Box>

          <Box className={styles.fieldGroup}>
            <Typography className={styles.fieldLabel}>
              {Vocabulary.toDate}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ro}>
              <DatePicker
                value={toDate}
                onChange={(date) => setToDate(date)}
                format="dd-MM-yyyy"
                slotProps={{
                  textField: { size: "medium", fullWidth: true },
                }}
              />
            </LocalizationProvider>
          </Box>

          <Box className={styles.fieldGroup}>
            <Typography className={styles.fieldLabel}>
              {Vocabulary.message}
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              value={smsMessage}
              onChange={(event) => setSmsMessage(event.target.value)}
              placeholder={Vocabulary.smsMessagePlaceholder}
            />
          </Box>

          <Box className={styles.actions}>
            <Button
              variant="outlined"
              onClick={handleApplyFilters}
              aria-label={Vocabulary.showClients}
            >
              {Vocabulary.showClients}
            </Button>
            <Button
              variant="contained"
              onClick={handleSendSms}
              disabled={!canSend}
              aria-label={Vocabulary.send}
            >
              {Vocabulary.send}
            </Button>
          </Box>
        </div>

        <div className={styles.rightPanel}>
          <MUIDataTableCustomStyle
            title={Vocabulary.clients}
            columns={clientsColumns}
            CRUDEndPoint={endpoints.ordersEndpoint}
            onEditAction={ETableEditAction.NONE}
            hideAddButton
            hideEditButton
            hideDeleteButton
            selectableRows="none"
            filters={filters}
            triggerReload={triggerReload}
          />
        </div>
      </div>
    </div>
  );
}

export default withRole(SmsClientsPage);
